import { Editor, Path, Text, Transforms } from 'slate'

import { TranscriptParagraphContent } from 'src/models/Transcript'
import { toSlateDescendant } from 'src/slate/types'

import { TranscriptEditor } from './TranscriptEditor'

const textLeaves = (editor: Editor): TranscriptParagraphContent[] =>
    [...Editor.nodes(editor, { at: [0], match: Text.isText })].map(([node]) => ({
        text: node.text,
        isEditable: node.isEditable === true,
    }))

const leavesMatch = (editor: Editor, content: readonly TranscriptParagraphContent[]): boolean => {
    const current = textLeaves(editor)

    return (
        current.length === content.length &&
        current.every((leaf, index) => leaf.text === content[index].text && leaf.isEditable === content[index].isEditable)
    )
}

const currentLeaves = (editor: Editor): [Text, Path][] => [...Editor.nodes(editor, { at: [0], match: Text.isText })] as [Text, Path][]

const restoreSelection = (editor: Editor, anchorOffset: number | null, focusOffset: number | null, nextLength: number): void => {
    if (anchorOffset === null || focusOffset === null) {
        return
    }

    const anchor = TranscriptEditor.getPointFromParagraphOffset(editor, [0], Math.min(anchorOffset, nextLength))
    const focus = TranscriptEditor.getPointFromParagraphOffset(editor, [0], Math.min(focusOffset, nextLength))
    if (!anchor || !focus) {
        editor.selection = null

        return
    }

    const clamped = TranscriptEditor.clampSelectionToEditable(editor, { anchor, focus })
    // Assign the range. Transforms.select asks Slate for the DOM node immediately, and a leaf that
    // was just created is not mounted yet.
    editor.selection = clamped
}

/**
 * Replace the editor leaves with `content` and put the selection back on the same characters.
 * The restored range is clamped onto editable leaves. A matching leaf list leaves the selection
 * object alone. The replacement is not written back to the doc.
 *
 * When the run count is unchanged, each leaf is edited in place. Replacing `editor.children`
 * drops the node out of Slate's DOM map, and the next keystroke throws "Cannot resolve a DOM node".
 */
export const reconcileParagraphLeaves = (editor: Editor, content: readonly TranscriptParagraphContent[]): void => {
    if (leavesMatch(editor, content)) {
        return
    }

    TranscriptEditor.withoutSyncEditorToTranscript(editor, () => {
        const selection = editor.selection
        const anchorOffset = selection === null ? null : TranscriptEditor.getParagraphOffsetFromPoint(editor, selection.anchor)
        const focusOffset = selection === null ? null : TranscriptEditor.getParagraphOffsetFromPoint(editor, selection.focus)
        const nextLength = content.reduce((sum, entry) => sum + entry.text.length, 0)
        const leaves = currentLeaves(editor)

        if (leaves.length === content.length) {
            Editor.withoutNormalizing(editor, () => {
                leaves.forEach(([node, path], index) => {
                    const next = content[index]
                    if (node.text !== next.text) {
                        if (node.text.length > 0) {
                            Transforms.delete(editor, { at: { path, offset: 0 }, distance: node.text.length, unit: 'character' })
                        }
                        if (next.text.length > 0) {
                            Transforms.insertText(editor, next.text, { at: { path, offset: 0 } })
                        }
                    }

                    const isEditable = next.isEditable === true
                    if (node.isEditable !== isEditable || node.leafKey !== index) {
                        Transforms.setNodes(editor, { isEditable, leafKey: index }, { at: path })
                    }
                })
            })
            restoreSelection(editor, anchorOffset, focusOffset, nextLength)
            editor.onChange()

            return
        }

        editor.children = [toSlateDescendant([...content])]
        restoreSelection(editor, anchorOffset, focusOffset, nextLength)
        // A caret that stays on the same point applies no operation, so Slate would keep painting
        // the previous leaves. Notify it after every replacement, including one with no selection.
        editor.onChange()
    })
}
